import React, {useEffect, useState} from "react";
import Headerbar from "../../navBars/Headerbar";
import {Layout, List, Popconfirm, Carousel, Modal, Input} from "antd";
import Sidebar from "../../navBars/Sidebar";
import {MDBCol, MDBIcon, MDBRow} from "mdbreact";
import {Grid, Card, Text} from "@nextui-org/react";
import {useToast} from "@chakra-ui/react";
import {useParams, useNavigate} from "react-router-dom";
import {useCollection, useDocumentData} from "react-firebase-hooks/firestore";
import {collection, doc, getFirestore, query, updateDoc, where,
        addDoc, deleteDoc, getDocs, deleteField} from "firebase/firestore";
import Firebase from "../../Firebase/Firebase";
import TransactionTable from "./TransactionTable";
import TransactionChart from "../Buyers/TransactionChart";
import {Tag} from "primereact/tag";
import {Rating} from "primereact/rating";
import moment from "moment";
import {logAdminAction} from "../../utils/auditLog";


const db = getFirestore(Firebase);
const {Content} = Layout;

const statusSeverity = (s: string): "success"|"warning"|"danger"|"info" => {
    const l = (s ?? '').toLowerCase();
    if (l === 'active')   return 'success';
    if (l === 'rejected') return 'danger';
    if (l === 'disabled') return 'warning';
    return 'info';
};

// A product is genuinely "on sale" only when isOnSale is backed by real sale
// data that lowers the price. Mirrors the apps (DiscountHelper / pricing.ts).
function isReallyOnSale(d: any): boolean {
    if (d?.isOnSale !== true) return false;
    const saleType: string = d.saleType ?? '';
    const salePrice = parseFloat(String(d.salePrice ?? ''));
    const percent = Number(d.discountPercent ?? 0);
    if (saleType === 'fixed') return !isNaN(salePrice) && salePrice > 0;
    if (saleType === 'percent') return percent > 0;
    return (!isNaN(salePrice) && salePrice > 0) || percent > 0;
}

const InfoRow = ({label, value}: {label: string; value: React.ReactNode}) => (
    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center',
                 padding:'9px 0', borderBottom:'1px solid #f5f5f5'}}>
        <span style={{fontSize:12, color:'#888', fontWeight:500, minWidth:110}}>{label}</span>
        <span style={{fontSize:13, color:'#222', fontWeight:500, textAlign:'right'}}>{value ?? '—'}</span>
    </div>
);

const ProductDetails = () => {
    const {id}      = useParams();
    const navigate  = useNavigate();
    const toast     = useToast();
    const [collapsed, setCollapsed]               = useState(false);
    const [product, setProduct]                   = useState<any>();
    const [reviewArray, setReviewArray]           = useState<any[]>([]);
    const [sellerArray, setSellerArray]           = useState<any[]>([]);
    const [transactionsArray, setTransactionsArray] = useState<any[]>([]);
    const [logsArray, setLogsArray]               = useState<any[]>([]);
    const [ordersArray, setOrdersArray]           = useState<any[]>([]);
    const [userArray, setUserArray]               = useState<any[]>([]);
    const [isFeatured, setIsFeatured]             = useState(false);
    const [featuredDocId, setFeaturedDocId]       = useState<string|null>(null);

    const uid = id?.trim() ?? '';

    const [value] = useDocumentData(doc(db, 'products', uid),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const [reviewSnapshot] = useCollection(
        query(collection(db, 'reviews'), where('objectId', '==', uid)),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const [sellerSnapshot] = useCollection(
        query(collection(db, 'users'), where('type', '==', 'Seller')),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const [orderSnapshot] = useCollection(
        query(collection(db, 'orders'), where('productId', '==', uid)),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const [buyerSnapshot] = useCollection(
        query(collection(db, 'users'), where('type', '==', 'buyer')),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const [featuredSnapshot] = useCollection(
        query(collection(db, 'featured_products'), where('productId', '==', uid)),
        {snapshotListenOptions: {includeMetadataChanges: true}});

    function fmt(x: any) {
        return x != null ? Number(x).toLocaleString() : '0';
    }

    useEffect(() => {
        if (value) setProduct(value);

        const sellers: any[] = [];
        sellerSnapshot?.docs.forEach(d => sellers.push(d.data()));
        setSellerArray(sellers);

        const buyers: any[] = [];
        buyerSnapshot?.docs.forEach(d => buyers.push(d.data()));
        setUserArray(buyers);

        const revs: any[] = [];
        reviewSnapshot?.docs.forEach(d => revs.push(d.data()));
        setReviewArray(revs);

        if (orderSnapshot) {
            const orders: any[] = [];
            orderSnapshot.docs.forEach(d => orders.push(d.data()));
            setOrdersArray(orders);
            setTransactionsArray(orders.map(o => ({
                product: o.items?.[0]?.productName ?? '—',
                amount:  `MK ${fmt(o.totalAmount)}`,
                buyer:   o.buyerName ?? o.buyername ?? '—',
                status:  <Tag severity={(o.orderStatus==='pending')?'warning':(o.orderStatus==='completed')?'success':'info'}
                              value={o.orderStatus}/>,
                date:    moment(o.createdAt).format('DD MMM YYYY'),
                payment: o.paymentMethods,
            })));
        }

        if (featuredSnapshot) {
            setIsFeatured(!featuredSnapshot.empty);
            setFeaturedDocId(featuredSnapshot.empty ? null : featuredSnapshot.docs[0].id);
        }
    }, [uid, value, reviewSnapshot, buyerSnapshot, orderSnapshot, sellerSnapshot, featuredSnapshot]);

    const [reasonModal, setReasonModal] = useState<{status: string} | null>(null);
    const [reasonText, setReasonText] = useState('');

    // reason is only meaningful for disabled/rejected — clears the field (rather
    // than leaving a stale reason behind) whenever a product is reactivated.
    const changeStatus = (status: string, reason?: string) => {
        updateDoc(doc(db, 'products', uid), {
            productStatus: status,
            moderationReason: reason ? reason : deleteField(),
        }).then(() => {
            logAdminAction('product_status_changed', {
                targetId: uid, targetName: product?.name,
                targetType: 'product', from: product?.productStatus, to: status,
                note: reason || undefined,
            });
            toast({position:'top', title:`Product ${status}`, status:'success',
                   duration:2000, isClosable:true});
        }).catch((e: any) => {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        });
    };

    const confirmReasonModal = () => {
        if (!reasonModal) return;
        changeStatus(reasonModal.status, reasonText.trim());
        setReasonModal(null);
        setReasonText('');
    };

    const toggleFeatured = async () => {
        try {
            if (isFeatured && featuredDocId) {
                await deleteDoc(doc(db, 'featured_products', featuredDocId));
                toast({position:'top', title:'Removed from featured', status:'success', duration:2000, isClosable:true});
            } else {
                const ref = await addDoc(collection(db, 'featured_products'), {
                    productId: uid,
                    dateAdded: new Date().toISOString(),
                });
                // update the doc with its own id
                await updateDoc(ref, {id: ref.id});
                toast({position:'top', title:'Added to featured', status:'success', duration:2000, isClosable:true});
            }
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        }
    };

    const isActive   = ['active','Active'].includes(product?.productStatus ?? '');
    const isDisabled = (product?.productStatus ?? '').toLowerCase() === 'disabled';
    const isRejected = (product?.productStatus ?? '').toLowerCase() === 'rejected';

    const avgRating = reviewArray.length
        ? (reviewArray.reduce((s, r) => s + (r.rating ?? 0), 0) / reviewArray.length).toFixed(1)
        : '—';

    const totalRevenue = ordersArray
        .filter(o => o.orderStatus === 'completed' || o.orderStatus === 'delivered')
        .reduce((s, o) => s + (Number(o.totalAmount) || 0), 0);

    const stats = [
        {title:'Orders',   icon:<MDBIcon icon="shopping-cart" size="2x" style={{color:'#29327b'}} className="mb-2"/>, number: ordersArray.length},
        {title:'Sales',    icon:<MDBIcon icon="check-circle"  size="2x" style={{color:'#29327b'}} className="mb-2"/>, number: ordersArray.filter(o=>o.orderStatus==='completed'||o.orderStatus==='delivered').length},
        {title:'Reviews',  icon:<MDBIcon icon="star"          size="2x" style={{color:'#29327b'}} className="mb-2"/>, number: reviewArray.length},
        {title:'Avg Rating',icon:<MDBIcon icon="thumbs-up"   size="2x" style={{color:'#29327b'}} className="mb-2"/>, number: avgRating},
    ];

    const seller = sellerArray.find(s => s.Uid === product?.sellerId);
    const images: string[] = product?.images ?? (product?.imageUrl ? [product.imageUrl] : []);

    const dateAdded = product?.dateAdded;
    const dateFormatted = dateAdded
        ? moment(dateAdded?.toDate ? dateAdded.toDate() : dateAdded).format('DD MMM YYYY')
        : '—';

    return (
        <>
        <Layout style={{minHeight:'100vh'}}>
            <Headerbar collapsed={collapsed} setCollapsed={setCollapsed}/>
            <Layout className="site-layout">
                <Sidebar collapsed={collapsed}/>
                <Content>
                    <div className="mt-4 mx-4">

                        {/* ── Back + title ─────────────────────────────────── */}
                        <div style={{display:'flex', alignItems:'center', gap:12, marginBottom:16}}>
                            <button onClick={() => navigate(-1)}
                                    style={{fontSize:12, color:'#29327b', border:'1px solid #c7d2fe',
                                            borderRadius:8, padding:'4px 14px', cursor:'pointer', background:'#eef2ff'}}>
                                ← Back
                            </button>
                            <span style={{fontWeight:700, fontSize:18, color:'#1a1a2e'}}>
                                {product?.name ?? 'Product'}
                            </span>
                            <Tag severity={statusSeverity(product?.productStatus ?? '')}
                                 value={product?.productStatus ?? '—'}/>
                            {isFeatured && (
                                <Tag severity="info" value="Featured"/>
                            )}
                            {isReallyOnSale(product) && (
                                <Tag severity="success" value="On sale"/>
                            )}
                        </div>

                        <MDBRow>
                            {/* ── Image gallery + details ─────────────────── */}
                            <MDBCol md="5">
                                {/* Images */}
                                <Card style={{marginBottom:12, overflow:'hidden', borderRadius:14}}>
                                    {images.length > 0 ? (
                                        <Carousel autoplay dotPosition="bottom">
                                            {images.map((img, i) => (
                                                <div key={i}>
                                                    <img src={img} alt={`product-${i}`}
                                                         style={{width:'100%', height:280, objectFit:'cover'}}/>
                                                </div>
                                            ))}
                                        </Carousel>
                                    ) : (
                                        <div style={{height:200, background:'#f5f5f5', display:'flex',
                                                     alignItems:'center', justifyContent:'center', color:'#ccc'}}>
                                            <MDBIcon icon="image" size="4x"/>
                                        </div>
                                    )}
                                </Card>

                                {/* Actions */}
                                <Card style={{marginBottom:12, overflow:'hidden', borderRadius:14}}>
                                    <div style={{padding:'12px 16px', borderBottom:'1px solid #f5f5f5',
                                                 fontWeight:700, fontSize:13, color:'#29327b'}}>
                                        Actions
                                    </div>
                                    <div style={{padding:'12px 16px', display:'flex', flexWrap:'wrap', gap:8}}>
                                        {!isActive && (
                                            <Popconfirm title="Activate this product listing?"
                                                        onConfirm={() => changeStatus('Active')}
                                                        okText="Activate" cancelText="Cancel">
                                                <button style={{fontSize:12, color:'#22c55e',
                                                                border:'1px solid #bbf7d0', borderRadius:7,
                                                                padding:'5px 14px', cursor:'pointer', background:'#f0fdf4'}}>
                                                    Activate
                                                </button>
                                            </Popconfirm>
                                        )}
                                        {isActive && (
                                            <button onClick={() => setReasonModal({status: 'disabled'})}
                                                    style={{fontSize:12, color:'#f59e0b',
                                                            border:'1px solid #fde68a', borderRadius:7,
                                                            padding:'5px 14px', cursor:'pointer', background:'#fffbeb'}}>
                                                Disable
                                            </button>
                                        )}
                                        {!isRejected && (
                                            <button onClick={() => setReasonModal({status: 'rejected'})}
                                                    style={{fontSize:12, color:'#ef4444',
                                                            border:'1px solid #fecaca', borderRadius:7,
                                                            padding:'5px 14px', cursor:'pointer', background:'#fef2f2'}}>
                                                Reject
                                            </button>
                                        )}
                                        <Popconfirm
                                            title={isFeatured
                                                ? 'Remove from featured products?'
                                                : 'Add to featured products on the home screen?'}
                                            onConfirm={toggleFeatured}
                                            okText="Yes" cancelText="Cancel">
                                            <button style={{
                                                fontSize:12,
                                                color: isFeatured ? '#6366f1' : '#29327b',
                                                border:`1px solid ${isFeatured ? '#c7d2fe' : '#c7d2fe'}`,
                                                borderRadius:7, padding:'5px 14px',
                                                cursor:'pointer',
                                                background: isFeatured ? '#eef2ff' : '#eef2ff',
                                            }}>
                                                {isFeatured ? 'Unfeature' : 'Feature'}
                                            </button>
                                        </Popconfirm>
                                    </div>
                                </Card>

                                {/* Info fields */}
                                <Card style={{overflow:'hidden', borderRadius:14}}>
                                    <div style={{padding:'12px 16px', borderBottom:'1px solid #f5f5f5',
                                                 fontWeight:700, fontSize:13, color:'#29327b'}}>
                                        Product Info
                                    </div>
                                    <div style={{padding:'4px 16px'}}>
                                        <InfoRow label="Status"
                                            value={<Tag severity={statusSeverity(product?.productStatus ?? '')}
                                                        value={product?.productStatus ?? '—'}/>}/>
                                        <InfoRow label="Price"     value={`MK ${fmt(product?.price)}`}/>
                                        <InfoRow label="Stock"     value={product?.quantity ?? product?.stock}/>
                                        <InfoRow label="Category"  value={product?.category}/>
                                        <InfoRow label="Sub-cat"   value={product?.subCategory}/>
                                        <InfoRow label="Condition" value={product?.condition}/>
                                        <InfoRow label="Location"  value={product?.district ?? product?.location}/>
                                        <InfoRow label="Seller"
                                            value={seller
                                                ? <button onClick={() => navigate(`/shops/${seller.Uid}`)}
                                                          style={{fontSize:12, color:'#29327b', border:'none',
                                                                  background:'none', cursor:'pointer', padding:0,
                                                                  textDecoration:'underline'}}>
                                                      {seller.fullName}
                                                  </button>
                                                : product?.sellerId}/>
                                        <InfoRow label="Listed"    value={dateFormatted}/>
                                        <InfoRow label="Avg Rating" value={avgRating}/>
                                        <InfoRow label="Revenue"   value={`MK ${fmt(totalRevenue)}`}/>
                                    </div>
                                    {product?.description && (
                                        <div style={{padding:'10px 16px', borderTop:'1px solid #f5f5f5'}}>
                                            <div style={{fontSize:11, color:'#aaa', fontWeight:500, marginBottom:4}}>
                                                DESCRIPTION
                                            </div>
                                            <p style={{fontSize:13, color:'#444', margin:0, lineHeight:1.6}}>
                                                {product.description}
                                            </p>
                                        </div>
                                    )}
                                </Card>
                            </MDBCol>

                            {/* ── Stats + Transactions + Activity + Reviews ── */}
                            <MDBCol md="7">
                                {/* Stat cards */}
                                <Grid.Container gap={1} justify="flex-start" className="mb-2">
                                    {stats.map((item, i) => (
                                        <Grid xs={6} key={i}>
                                            <Card className="font-JosefinSans" style={{minHeight:90}}>
                                                <Card.Body>
                                                    <div className="d-flex flex-column justify-content-start px-1">
                                                        {item.icon}
                                                        <Text b size={12} className="mt-1">{item.title}</Text>
                                                        <Text b size={18} css={{color:'#29327b'}}>{item.number}</Text>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid.Container>

                                {/* Orders */}
                                <Card className="my-2 font-JosefinSans overflow-hidden" isHoverable>
                                    <div style={{fontWeight:600, fontSize:14, padding:'12px 16px',
                                                 borderBottom:'1px solid #f5f5f5', color:'#29327b'}}>
                                        Orders ({ordersArray.length})
                                    </div>
                                    <Card.Body>
                                        <TransactionTable data={transactionsArray}/>
                                    </Card.Body>
                                </Card>

                                {/* Activity chart */}
                                <Card className="my-2 font-JosefinSans overflow-hidden" isHoverable>
                                    <Card.Header>
                                        <Text b size={15} className="m-2">Activity (Past 6 Months)</Text>
                                    </Card.Header>
                                    <Card.Body>
                                        <TransactionChart logs={logsArray} orders={ordersArray}/>
                                    </Card.Body>
                                </Card>

                                {/* Reviews */}
                                <Card className="my-2 font-JosefinSans overflow-hidden" isHoverable>
                                    <div style={{fontWeight:600, fontSize:14, padding:'12px 16px',
                                                 borderBottom:'1px solid #f5f5f5', color:'#29327b'}}>
                                        Reviews ({reviewArray.length})
                                    </div>
                                    <Card.Body>
                                        <List
                                            itemLayout="vertical"
                                            pagination={{pageSize:4, size:'small'}}
                                            dataSource={reviewArray}
                                            locale={{emptyText:'No reviews yet'}}
                                            renderItem={item => {
                                                const buyer = userArray.find(u => u.Uid === item.buyerId);
                                                const dateVal = item.createdAt?.toDate
                                                    ? item.createdAt.toDate() : item.createdAt;
                                                return (
                                                    <List.Item key={item.id}>
                                                        <div style={{display:'flex', gap:10, alignItems:'flex-start'}}>
                                                            <div style={{width:36, height:36, borderRadius:'50%',
                                                                         background:'#eef2ff', display:'flex',
                                                                         alignItems:'center', justifyContent:'center',
                                                                         color:'#29327b', fontWeight:700, fontSize:14, flexShrink:0}}>
                                                                {buyer?.fullName?.charAt(0)?.toUpperCase() ?? '?'}
                                                            </div>
                                                            <div style={{flex:1}}>
                                                                <div style={{fontWeight:600, fontSize:13}}>
                                                                    {buyer?.fullName ?? 'Anonymous'}
                                                                </div>
                                                                <div style={{display:'flex', gap:8, alignItems:'center', marginTop:2}}>
                                                                    <Rating value={item.rating} readOnly stars={5} cancel={false}/>
                                                                    <span style={{fontSize:11, color:'#aaa'}}>
                                                                        {dateVal ? moment(dateVal).format('DD MMM YYYY') : '—'}
                                                                    </span>
                                                                </div>
                                                                {item.description && (
                                                                    <p style={{fontSize:12, color:'#555', marginTop:4,
                                                                               overflow:'hidden', display:'-webkit-box',
                                                                               WebkitLineClamp:2, WebkitBoxOrient:'vertical'}}>
                                                                        {item.description}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </List.Item>
                                                );
                                            }}
                                        />
                                    </Card.Body>
                                </Card>
                            </MDBCol>
                        </MDBRow>
                    </div>
                </Content>
            </Layout>
        </Layout>

        <Modal
            open={reasonModal !== null}
            title={reasonModal?.status === 'disabled' ? 'Disable this product?' : 'Reject this product?'}
            onCancel={() => { setReasonModal(null); setReasonText(''); }}
            onOk={confirmReasonModal}
            okText={reasonModal?.status === 'disabled' ? 'Disable' : 'Reject'}
            okButtonProps={{danger: true}}
        >
            <div style={{fontSize: 13, color: '#666', marginBottom: 8}}>
                Buyers will not see this product. Give the seller a reason — they&apos;ll see it on the
                product&apos;s edit screen and can appeal from there.
            </div>
            <Input.TextArea
                rows={3}
                placeholder="Reason (shown to the seller)"
                value={reasonText}
                onChange={(e) => setReasonText(e.target.value)}
            />
        </Modal>
        </>
    );
};

export default ProductDetails;
